import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Sparkles, MapPin, Briefcase, Loader2, Send, RotateCcw } from "lucide-react";

export default function SuggestedJobsWidget() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(null); // job id being processed
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [letter, setLetter] = useState("");
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api
      .get("/jobs/suggested")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  };

  useEffect(load, []);

  const startApply = async (match) => {
    setActiveJob(match.job);
    setDialogOpen(true);
    setLetter("");
    setGenerating(true);
    setLoadingMatch(match.job.id);
    try {
      const { data } = await api.post("/ai/cover-letter-for-job", {
        job_id: match.job.id,
        language: i18n.language || "fr",
      });
      setLetter(data.cover_letter || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("suggested.gen_error"));
      setDialogOpen(false);
    } finally {
      setGenerating(false);
      setLoadingMatch(null);
    }
  };

  const regenerate = async () => {
    if (!activeJob) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/cover-letter-for-job", {
        job_id: activeJob.id,
        language: i18n.language || "fr",
      });
      setLetter(data.cover_letter || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("suggested.gen_error"));
    } finally {
      setGenerating(false);
    }
  };

  const submitApplication = async () => {
    if (!activeJob || !letter.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/applications", { job_id: activeJob.id, cover_letter: letter });
      toast.success(t("suggested.applied"));
      setDialogOpen(false);
      setItems((prev) => (prev || []).filter((m) => m.job.id !== activeJob.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (items === null) return null;
  if (items.length === 0) return null;

  return (
    <div className="mec-card p-6 mt-4" data-testid="suggested-jobs-widget">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="mec-overline mb-1">{t("suggested.overline")}</div>
          <h2 className="font-display text-xl font-bold text-slate-900">{t("suggested.title")}</h2>
        </div>
        <Sparkles className="w-5 h-5 text-amber-500" />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {items.map((m) => (
          <div
            key={m.job.id}
            className="border border-slate-200 rounded-lg p-4 hover:border-[#004EEB] hover:bg-blue-50/30 transition group"
            data-testid={`suggested-job-${m.job.id}`}
          >
            <div className="flex items-start justify-between mb-2">
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 text-[10px]">
                {m.match_percent}% match
              </Badge>
              {m.job.remote && (
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">remote</span>
              )}
            </div>
            <button
              onClick={() => nav(`/jobs/${m.job.id}`)}
              className="font-display font-semibold text-slate-900 hover:text-[#004EEB] text-left text-base leading-snug mb-1 line-clamp-2"
              data-testid={`suggested-title-${m.job.id}`}
            >
              {m.job.title}
            </button>
            <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
              <span className="flex items-center gap-1">
                <Briefcase className="w-3 h-3" /> {m.job.employer_name}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {m.job.country_code}
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => startApply(m)}
              disabled={loadingMatch === m.job.id}
              className="w-full bg-[#004EEB] hover:bg-[#003BCC] text-white text-xs"
              data-testid={`apply-1click-${m.job.id}`}
            >
              {loadingMatch === m.job.id ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                  {t("suggested.preparing")}
                </>
              ) : (
                <>
                  <Sparkles className="w-3 h-3 mr-1.5" />
                  {t("suggested.apply_1click")}
                </>
              )}
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="apply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {t("suggested.dialog_title")} — {activeJob?.title}
            </DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              {activeJob?.employer_name} · {activeJob?.country_code}
            </p>
          </DialogHeader>

          <div className="mt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">
                {t("suggested.letter_label")}
              </label>
              <Button
                variant="ghost"
                size="sm"
                onClick={regenerate}
                disabled={generating}
                className="text-xs h-7"
                data-testid="regenerate-btn"
              >
                <RotateCcw className={`w-3 h-3 mr-1 ${generating ? "animate-spin" : ""}`} />
                {t("suggested.regenerate")}
              </Button>
            </div>
            {generating ? (
              <div className="border border-slate-200 rounded-md p-8 flex flex-col items-center justify-center text-slate-500 text-sm h-64">
                <Loader2 className="w-6 h-6 animate-spin text-[#004EEB] mb-2" />
                {t("suggested.generating")}
              </div>
            ) : (
              <textarea
                value={letter}
                onChange={(e) => setLetter(e.target.value)}
                rows={12}
                className="w-full border border-slate-300 rounded-md p-3 text-sm leading-relaxed focus:border-[#004EEB] focus:ring-1 focus:ring-[#004EEB] outline-none resize-y"
                placeholder={t("suggested.letter_placeholder")}
                data-testid="letter-textarea"
              />
            )}
            <p className="text-[11px] text-slate-500 mt-2">{t("suggested.letter_hint")}</p>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-apply-btn">
              {t("common.cancel")}
            </Button>
            <Button
              onClick={submitApplication}
              disabled={submitting || generating || !letter.trim()}
              className="bg-[#004EEB] hover:bg-[#003BCC] text-white"
              data-testid="confirm-apply-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  {t("common.submitting")}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-1.5" />
                  {t("suggested.send_application")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
